import Image from "@/components/Atoms/Image";
import { useState } from "react";
import { TagLabel } from "@/components/Atoms/Label";
import styles from "./index.module.scss";
import RecipeCard from "../RecipeCard";
import { motion } from "framer-motion";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { useRouter } from "next/router";
import {
  resolveButtonCustomIcon,
  resolveButtonIconColor,
  resolvePageRelatedCta,
} from "@/lib/functions";

interface RecipeBlockProps {
  data: any;
  layout: "default" | "featured" | "gridRounded" | null | undefined;
  color: any;
  excerpt: any;
  cta: any;
  highlight: any;
  load: any;
  labelColor: any;
}

function getRecipeCardImage(post: any) {
  return post?.image ?? post?.featuredImage;
}

function getRecipeCardImageStyle(cardImage: any): React.CSSProperties {
  return {
    "--x": cardImage?.focalPoint?.x ?? 0.5,
    "--y": cardImage?.focalPoint?.y ?? 0.5,
  } as React.CSSProperties;
}

function compareRecipeOrder(a: any, b: any) {
  const orderA = Number.isFinite(a?.order) ? a.order : Infinity;
  const orderB = Number.isFinite(b?.order) ? b.order : Infinity;
  return orderA - orderB;
}

export default function RecipeBlock({
  data,
  layout,
  color,
  excerpt,
  cta,
  highlight,
  load,
  labelColor,
}: RecipeBlockProps) {
  // Initial state for displayed posts count
  const [visiblePostsCount, setVisiblePostsCount] = useState(4);
  const showPrepHighlights = data?.brandRecipe?.showPrepHighlights;

  // Function to load more posts
  const loadMorePosts = () => {
    setVisiblePostsCount((prevCount) => prevCount + 4);
  };

  const router: any = useRouter();
  const cardCta = resolvePageRelatedCta(cta);
  const loadCta = resolvePageRelatedCta(load);

  const noSlugRecipes = (data.allRecipes ?? [])
    .filter((recipes: any) => recipes.slug !== router.query?.slug?.[0])
    .sort(compareRecipeOrder);

  switch (layout) {
    case "default":
      return (
        <div className="container mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {noSlugRecipes
            .slice(0, visiblePostsCount)
            .map((post: any, i: number) => {
              const cardImage = getRecipeCardImage(post);
              const style = getRecipeCardImageStyle(cardImage);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className={"col-span-1 flex flex-col bg-white"}
                  key={i}
                >
                  <div className={`${styles.imageContainerGrid} relative`}>
                    <TagLabel
                      highlight={highlight}
                      label={post.layoutType}
                      color={color}
                    />
                    <div style={style} className={styles.imageContainer}>
                      <Image
                        layout="fill"
                        objectFit="cover"
                        data={cardImage}
                      />
                    </div>
                  </div>
                  <div className=" mx-auto w-full relative">
                    <RecipeCard
                      layout={layout}
                      post={post}
                      cta={cardCta}
                      color={color}
                      excerpt={excerpt}
                      showPrepHighlights={showPrepHighlights}
                    />
                  </div>
                </motion.div>
              );
            })}
          {visiblePostsCount < data.allRecipes.length && loadCta && (
            <div className="w-full mx-auto flex justify-center py-4   md:col-span-2 lg:col-span-4">
              <ButtonMaster
                onClick={loadMorePosts}
                type={loadCta.buttonStyle}
                cta={loadCta.typeColor?.color}
                color={loadCta.labelColor?.color}
                customIcon={resolveButtonCustomIcon(loadCta)}
                customColor={resolveButtonIconColor(loadCta)}
                link={``}
              >
                {loadCta.buttonLabel?.label}
              </ButtonMaster>
            </div>
          )}
        </div>
      );
    case "featured":
      return (
        <div className="container mx-auto grid grid-cols-2 gap-6 mt-6">
          {noSlugRecipes
            .slice(0, visiblePostsCount)
            .map((post: any, i: number) => {
              const cardImage = getRecipeCardImage(post);
              const style = getRecipeCardImageStyle(cardImage);
              if (i == 0) {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className={"grid col-span-2 grid-cols-5"}
                    key={i}
                  >
                    <div className="col-span-3 relative">
                      <TagLabel
                        highlight={highlight}
                        label={post.layoutType}
                        color={color}
                      />

                      <div style={style} className={styles.imageContainerFeatured}>
                        <Image
                          className={`top-0 w-full h-full object-center md:object-bottom lg:min-h-[25rem] object-cover`}
                          data={cardImage}
                        />
                      </div>
                      <RecipeCard
                        layout={layout}
                        post={post}
                        cta={cardCta}
                        color={color}
                        excerpt={excerpt}
                        showPrepHighlights={showPrepHighlights}
                      />
                    </div>
                  </motion.div>
                );
              } else {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className={"grid grid-cols-2"}
                    key={i}
                  >
                    <div className="col-span-2 relative">
                      <TagLabel
                        highlight={highlight}
                        label={"Recipe"}
                        color={color}
                      />
                      <div style={style} className={styles.imageContainerFeatured}>
                        <Image
                          className={`top-0 w-full h-full object-center md:object-bottom lg:min-h-[25rem] object-cover`}
                          data={cardImage}
                        />
                      </div>
                      <RecipeCard
                        post={post}
                        cta={cardCta}
                        color={color}
                        excerpt={excerpt}
                        showPrepHighlights={showPrepHighlights}
                      />
                    </div>
                  </motion.div>
                );
              }
            })}
          {visiblePostsCount < data.allRecipes.length && loadCta && (
            <div className="w-full mx-auto flex justify-center py-4  md:col-span-2 lg:col-span-4">
              <ButtonMaster
                onClick={loadMorePosts}
                type={loadCta.buttonStyle}
                cta={loadCta.typeColor?.color}
                color={loadCta.labelColor?.color}
                customIcon={resolveButtonCustomIcon(loadCta)}
                customColor={resolveButtonIconColor(loadCta)}
                link={``}
              >
                {loadCta.buttonLabel?.label}
              </ButtonMaster>
            </div>
          )}
        </div>
      );
    default:
      return (
        <div className="container mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 ">
          {noSlugRecipes
            .slice(0, visiblePostsCount)
            .map((post: any, i: number) => {
              const cardImage = getRecipeCardImage(post);
              const style = getRecipeCardImageStyle(cardImage);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className={
                    "col-span-1 flex flex-col rounded-md overflow-hidden bg-white"
                  }
                  key={i}
                >
                  <div className={`${styles.imageContainerGrid} relative`}>
                    <TagLabel
                      layout="gridRounded"
                      highlight={highlight}
                      label={post.layoutType}
                      color={labelColor}
                    />
                    <div style={style} className={styles.imageContainer}>
                      <Image
                        layout="fill"
                        objectFit="cover"
                        data={cardImage}
                      />
                    </div>
                  </div>
                  <div className=" mx-auto w-full relative">
                    <RecipeCard
                      layout={layout}
                      post={post}
                      cta={cardCta}
                      color={color}
                      excerpt={excerpt}
                      labelColor={labelColor}
                      showPrepHighlights={showPrepHighlights}
                    />
                  </div>
                </motion.div>
              );
            })}
          {visiblePostsCount < data.allRecipes.length && loadCta && (
            <div className="w-full mx-auto flex justify-center py-4   md:col-span-2 lg:col-span-4">
              <ButtonMaster
                onClick={loadMorePosts}
                type={loadCta.buttonStyle}
                cta={loadCta.typeColor?.color}
                color={loadCta.labelColor?.color}
                customIcon={resolveButtonCustomIcon(loadCta)}
                customColor={resolveButtonIconColor(loadCta)}
                link={``}
              >
                {loadCta.buttonLabel?.label}
              </ButtonMaster>
            </div>
          )}
        </div>
      );
  }
}
